// A public key's form, shared by its card on the Security page
// (key-section.tsx): a saved key is renamed only — its type and PEM are what
// signatures were made against — while a new one also takes both. Nothing
// here is confidential; that is the point of verifying against a public key
// rather than sharing a secret.

import { Input, Select, Textarea } from "@pej/kore/components";
import { z } from "zod";
import { LAST_KEY_REFUSAL } from "@/api/friendly-error";
import type { ContextKey } from "@/api/types";
import { Field, useFormContext } from "@/lib/forms";

const ALGORITHMS = [
    { value: "EdDSA", label: "Ed25519 (EdDSA)" },
    { value: "RS256", label: "RSA (RS256)" },
];

export const NEW_KEY_DEFAULTS = { label: "", algorithm: "EdDSA", publicKey: "" };

/** A saved key's form: renamed only, so the label is the one thing checked
 *  and sent. Its type and PEM aren't fields here at all — a hidden, empty
 *  PEM must never stop a rename from submitting. */
export const labelSchema = z.object({
    // No client-side length rule: the server's 1–80 rule is the one that
    // speaks, so its wording is what a too-long label shows.
    label: z.string().trim().min(1, "Give it a name you'll recognise"),
});
export type LabelValues = z.infer<typeof labelSchema>;

/** A new key's form: the label, plus the type and PEM it is created with. */
export const keySchema = labelSchema.extend({
    algorithm: z.string(),
    publicKey: z.string().trim().min(1, "Paste the public key"),
});
export type KeyValues = z.infer<typeof keySchema>;

/** A saved key is renamed only; a new one also takes its type and PEM. */
export function KeyFields({ isDraft }: { isDraft: boolean }) {
    const { control } = useFormContext<KeyValues>();
    return (
        <div className="flex flex-col gap-4">
            <Field
                name="label"
                control={control}
                label="Name"
                required
                description="Whose key this is, so you can tell two apart."
                render={({ field }) => <Input {...field} placeholder="Ordering platform" />}
            />
            {isDraft && (
                <>
                    <Field
                        name="algorithm"
                        control={control}
                        label="Type"
                        render={({ field: { value, onChange, name } }) => (
                            <Select value={value} onValueChange={onChange} name={name}>
                                <Select.Trigger>
                                    <Select.Value>
                                        {(v: unknown) => ALGORITHMS.find((a) => a.value === v)?.label ?? String(v)}
                                    </Select.Value>
                                </Select.Trigger>
                                <Select.Content>
                                    {ALGORITHMS.map((algorithm) => (
                                        <Select.Item key={algorithm.value} value={algorithm.value}>
                                            {algorithm.label}
                                        </Select.Item>
                                    ))}
                                </Select.Content>
                            </Select>
                        )}
                    />
                    <Field
                        name="publicKey"
                        control={control}
                        label="Public key"
                        required
                        description="The PEM block, beginning BEGIN PUBLIC KEY."
                        render={({ field }) => (
                            <Textarea
                                {...field}
                                rows={5}
                                placeholder="-----BEGIN PUBLIC KEY-----"
                                // kore's Form.Control hands the generated id to
                                // Input but not to Textarea, leaving the label
                                // orphaned; name it directly until that is fixed.
                                aria-label="Public key"
                            />
                        )}
                    />
                </>
            )}
        </div>
    );
}

export function whenUsed(key: ContextKey): string {
    if (!key.lastVerifiedAt) return "Never used";
    const at = new Date(key.lastVerifiedAt);
    return Number.isNaN(at.getTime()) ? "Never used" : `Last used ${at.toLocaleString()}`;
}

/** Why the key can't be removed (the server refuses it too), or undefined. */
export function removeRefusal(keys: readonly ContextKey[], requireSignedContext: boolean): string | undefined {
    return keys.length === 1 && requireSignedContext ? LAST_KEY_REFUSAL : undefined;
}
